import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { Calendar, CalendarOptions, EventClickArg, FullCalendarComponent } from '@fullcalendar/angular';
import { DateClickArg } from '@fullcalendar/interaction';
import { Select, Store } from '@ngxs/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { ModalService } from 'src/shared/services';
import { AuthState } from 'src/shared/store/state/auth.state';
import { GetCustodies, ResetCustody } from './../../store/actions/users.action';

import { BaseComponent } from '../../abstracts/base.abstract';
import {
    ICustodyAttributesDTO,
    ICustodyDTOIn,
    ICustodyDTOOut,
    ICustodyItem,
    IGardeCalendarDate,
    IGardeModel,
} from '../../interfaces/garde.interface';
import { IUser } from '../../interfaces/users/user.interface';
import { PreventGoBack } from '../../store/actions/back-button.action';
import { ResetWaitingCustody, SaveCustody } from '../../store/actions/users.action';
import { UsersState } from '../../store/state/users.state';
import { applyCustodyModel, custodyModels } from '../../utils/calendar-custodies.utils';
import {
    formatDateDDMMYYYY,
    formatDateYYYYMMDD, getFirstDayOfNextSchoolYear, getFirstDayOfSchoolYear,
    getLastDayOfSchoolYear,
    getMonthTrad,
    getNumberOfDaysScholarYear,
    parseDDMMYYYToDate,
} from '../../utils/date.utils';

@Component({
    selector: 'app-gardes',
    templateUrl: './gardes.component.html',
    styleUrls: ['./gardes.component.scss'],
})
export class GardesComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;
    @Select(UsersState.getCustodies) public custodies$: Observable<ICustodyDTOIn>;
    @Select(UsersState.getWaitingCustodies) public waitingCustodies$: Observable<ICustodyDTOOut[]>;
    @Select(UsersState.getStudentIdOfWaitingCustodies) public studentIdOfWaitingCustodies$: Observable<number>;

    @ViewChild('calendar') public calendarComponent: FullCalendarComponent;

    @Input() public readonly = false;

    public parentsEntities$ = new BehaviorSubject<IUser[]>([]);
    public parents: IUser[] = [];

    public gardeModels: IGardeModel[] = custodyModels;

    public isSmallScreen = false;
    public isExtraSmallScreen = false;

    public currentUser: IUser;
    public selectedParent: IUser;
    public waitingCustInitialized: boolean;

    public calendarOptions: CalendarOptions;
    public calendarAPI: Calendar;
    public firstDayOfCurrentMonth: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    public today = new Date();
    public currentMonthTrad = getMonthTrad(this.today.getMonth());
    public periodForm: FormGroup; // year or month
    public isLoading = false; // true while adding custodies events
    public studentId: number;

    // dates and gardes
    public custodies: ICustodyDTOIn;
    public allDates: IGardeCalendarDate[] = []; // all dates in calendar

    // dangerous actions check
    public areYouSureToReinitModif: boolean = false;
    public areYouSureToReinitEverything: boolean = false;
    public isSaved = false;
    public errorAllDates = false;

    private isFullYear = true;
    private currentGardeModel: IGardeModel;

    @HostListener('window:resize')
    public onresize(): void {
        this.isSmallScreen = window.innerWidth <= 768 || this.deviceDetector.isMobile();
        this.isExtraSmallScreen = window.innerWidth <= 576 || this.deviceDetector.isMobile();
    }

    public get periodForModel(): FormControl {
        return this.periodForm.get('year') as FormControl;
    }

    constructor(
        private fb: FormBuilder,
        private store: Store,
        private deviceDetector: DeviceDetectorService,
        private cdr: ChangeDetectorRef,
        private modalService: ModalService,
        private route: ActivatedRoute
    ) {
        super();
    }

    public ngOnInit(): void {
        this.onresize();
        this.periodForm = this.fb.group({
            year: [true],
        });
        this.isFullYear = true;
        this.uns = this.parentsEntities$.subscribe((p: IUser[]) => {
            this.selectedParent = p[0];
            this.parents = p;
        });
        this.initCalendarOptions();
        this.uns = this.periodForm.get('year').valueChanges.subscribe((val: boolean) => (this.isFullYear = val));
        this.uns = combineLatest([this.studentIdOfWaitingCustodies$, this.route.queryParams]).subscribe(
            ([id, params]: [number, Params]) => {
                if (params?.id) {
                    // si edition
                    this.studentId = +params.id;
                    if (+params.id !== id) {
                        this.store.dispatch(new ResetWaitingCustody());
                    }
                    this.store.dispatch(new GetCustodies(+params.id));
                } else if (!params.id && id) {
                    // si creation avec un précédent waiting custody d'une edition
                    this.store.dispatch([new ResetCustody(), new ResetWaitingCustody()]);
                } else {
                    // si creation sans waiting custody d'une edition
                    this.store.dispatch([new ResetCustody()]);
                }
            }
        );
    }

    public handleResize(): any {
        this.calendarAPI.setOption('aspectRatio', this.isExtraSmallScreen ? 0.75 : this.isSmallScreen ? 1 : 1.25);
    }

    public ngAfterViewInit(): void {
        this.calendarAPI = this.calendarComponent.getApi();
        this.calendarAPI.next();
        this.calendarAPI.gotoDate(new Date());

        this.uns = combineLatest([this.currentUser$, this.custodies$, this.waitingCustodies$, this.parentsEntities$]).subscribe(
            ([user, cust, waitingCust, parents]: [IUser, ICustodyDTOIn, ICustodyDTOOut[], IUser[]]) => {
                if (user) {
                    this.currentUser = user;
                }
                if (waitingCust?.length > 0 && !this.waitingCustInitialized && parents.length) {
                    if (this.allDates.length > 0) {
                        this.resetModif();
                    }
                    this.initWaitingCustody(waitingCust[waitingCust.length - 1]);
                    this.waitingCustInitialized = true;
                }
                if (cust && (!waitingCust || waitingCust?.length === 0) && parents.length) {
                    this.selectedParent = parents.find((parent: IUser) => cust.student_custody.parent_one_id === parent.id) || parents[0];
                    this.custodies = cust;
                    if (this.allDates.length > 0) {
                        this.resetModif();
                    }
                    this.initCustody(this.custodies);
                }
            }
        );
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public handleDateClick(arg: DateClickArg): void {
        if (this.readonly) {
            return;
        }
        this.errorAllDates = false;
        this.isFullYear = false;
        if (this.calendarAPI.getEventById(arg.dateStr)) {
            // si date existante, la remove
            this.removeGardeDate(arg.date);
        } else {
            // sinon l'ajouter
            this.addGardeDate(arg.date, this.selectedParent.id, this.selectedParent.firstname);
        }
    }

    public handleEventClick(arg: EventClickArg): void {
        if (this.readonly) {
            return;
        }
        this.removeGardeDate(arg.event.start);
    }

    public handleMonthChange(): void {
        if (this.calendarAPI) {
            const dateCalendar: Date = this.calendarAPI.getDate();
            this.firstDayOfCurrentMonth = new Date(dateCalendar.getFullYear(), dateCalendar.getMonth(), 1);
        } else {
            this.firstDayOfCurrentMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
        }
        this.currentMonthTrad = getMonthTrad(this.firstDayOfCurrentMonth.getMonth());
    }

    public applyGardeModel(gm: IGardeModel): void {
        if (this.isLoading) {
            return;
        }
        this.isLoading = true;
        setTimeout(() => this.loadGM(gm), 100);
        this.cdr.detectChanges();
    }

    public initCustody(c: ICustodyDTOIn): void {
        if (c.student_custody.type && c.student_custody.type !== 4) {
            this.periodForm.get('year').patchValue(true);
            const model = this.gardeModels.find((garde: IGardeModel) => garde.id === c.student_custody.type);
            if (model) {
                this.loadGM(model, true);
            }
        } else if (c.custody_items?.length > 0) {
            c.custody_items.forEach((ci: ICustodyItem) => {
                const parent: IUser = this.parents[0].id === ci.parent_id ? this.parents[0] : this.parents[1];
                if (parent) {
                    this.addGardeDate(ci.date, parent.id, parent.firstname);
                }
            });
        }
    }

    public initWaitingCustody(c: ICustodyDTOOut): void {
        if (c.student_custody.custody_type_id && c.student_custody.custody_type_id !== 4) {
            this.periodForm.get('year').patchValue(true);
            const model = this.gardeModels.find((garde: IGardeModel) => garde.id === c.student_custody.custody_type_id);
            if (model) {
                this.loadGM(model, true);
            }
        } else if (c.student_custody.custody_items_attributes?.length > 0) {
            c.student_custody.custody_items_attributes.forEach((ci: ICustodyAttributesDTO) => {
                const parent: IUser = this.parents[0].id === ci.parent_id ? this.parents[0] : this.parents[1];
                this.addGardeDate(parseDDMMYYYToDate(ci.date), parent.id, parent.firstname, true);
            });
        }
    }

    public submit(): void {
        this.errorAllDates = false;
        if (getNumberOfDaysScholarYear() !== this.allDates.length) {
            this.errorAllDates = true;
            return;
        }
        // sauvegarder les gardes
        const custodyAttributes: ICustodyAttributesDTO[] = this.allDates.map((d: IGardeCalendarDate) => ({
            date: formatDateDDMMYYYY(d.date),
            parent_id: d.parent_id,
        }));
        const custody: ICustodyDTOOut = {
            student_custody: {
                custody_type_id: this.isFullYear && this.currentGardeModel ? this.currentGardeModel.id : 4,
                custody_items_attributes: custodyAttributes,
                parent_one_id: this.selectedParent.id,
            },
        };
        this.store.dispatch([new SaveCustody(custody, this.studentId), new PreventGoBack()]).subscribe(() => {
            // just for ergonomy
            this.isSaved = true;
            setTimeout(() => {
                this.modalService.closeModal();
            }, 2000);
        });
    }

    /**
     * Remove all events in calendar
     * and do not reinit previous saved dates
     */
    public reinitCalendar(): void {
        this.areYouSureToReinitModif = false;
        this.areYouSureToReinitEverything = false;
        this.allDates = [];
        if (this.calendarAPI) {
            this.calendarAPI.removeAllEvents();
        }
        this.store.dispatch([new ResetCustody(), new ResetWaitingCustody(), new PreventGoBack()]);
    }

    /**
     * Remove all modif in calendar
     * then reinit previous saved dates
     */
    public reinitModifs(): void {
        this.areYouSureToReinitModif = false;
        this.areYouSureToReinitEverything = false;
        this.allDates = [];
        if (this.calendarAPI) {
            this.calendarAPI.removeAllEvents();
        }
        this.store.dispatch(new ResetWaitingCustody());
        this.initCustody(this.custodies);
    }

    public resetModif(): void {
        this.areYouSureToReinitModif = false;
        this.areYouSureToReinitEverything = false;
        this.allDates = [];
        if (this.calendarAPI) {
            this.calendarAPI.removeAllEvents();
        }
    }

    private initCalendarOptions(): void {
        this.calendarOptions = {
            initialView: 'dayGridMonth',
            dateClick: this.handleDateClick.bind(this),
            eventClick: this.handleEventClick.bind(this),
            datesSet: this.handleMonthChange.bind(this),
            firstDay: 1,
            locale: 'fr',
            handleWindowResize: true,
            windowResize: () => {
                this.handleResize();
            },
            aspectRatio: this.isExtraSmallScreen ? 0.75 : this.isSmallScreen ? 1 : 1.25,
            showNonCurrentDates: false,
            events: [],
            validRange: () => ({
                start: getFirstDayOfSchoolYear(),
                end: getLastDayOfSchoolYear(),
            }),
        };
    }

    private loadGM(gm: IGardeModel, fullYearFromSeptember = false): void {
        this.errorAllDates = false;
        this.currentGardeModel = gm;
        const isYear = fullYearFromSeptember || !!this.periodForm?.get('year')?.value || this.isFullYear;
        this.isFullYear = isYear;
        if (isYear) {
            this.resetModif();
        }
        const startDate = isYear ? null : (this.firstDayOfCurrentMonth || new Date(this.today.getFullYear(), this.today.getMonth(), 1));
        const endDate = isYear
            ? null
            : getFirstDayOfNextSchoolYear();

        const gardeEvents: IGardeCalendarDate[] = applyCustodyModel(
            gm.id,
            this.parents,
            this.selectedParent.id,
            startDate,
            endDate
        );

        for (const gardeEvent of gardeEvents) {
            const parent: IUser = this.parents.find((p: IUser) => p.id === gardeEvent.parent_id);
            this.addGardeDate(gardeEvent.date, gardeEvent.parent_id, parent.firstname, true);
        }
        this.isLoading = false;
        this.cdr.detectChanges();
    }

    /**
     * Add a date to calendar and in the garde dates list
     *
     * @param date the date to add
     * @param parentId in charge for this date
     * @param title displayed for the event in the calendar
     * @param override if true and if event already exist for this date, remove the event and add the new one
     */
    private addGardeDate(date: Date, parentId: number, title: string, override?: boolean): void {
        if (this.calendarAPI.getEventById(formatDateYYYYMMDD(date)) && override) {
            this.removeGardeDate(date);
        }
        this.calendarAPI.addEvent({
            id: formatDateYYYYMMDD(date),
            title,
            color: this.parents[0].id === parentId ? '#00BCD4' : '#9C27B0',
            start: formatDateYYYYMMDD(date),
            allDay: true,
        });
        this.allDates.push({
            date: new Date(date),
            parent_id: parentId,
        });
    }

    /**
     * Remove date from calendar and garde dates list
     *
     * @param date to remove
     */
    private removeGardeDate(date: Date): void {
        this.calendarAPI.getEventById(formatDateYYYYMMDD(date))?.remove();
        this.allDates = this.allDates.filter((d: IGardeCalendarDate) => formatDateYYYYMMDD(date) !== formatDateYYYYMMDD(d.date));
    }
}
